import type { LinkVariantProps } from '@/hen-ui/atomics/Link';
import type { NavItem, SubNavLinkHover } from './types';

export const FOCUS_RING =
  'rounded-xs focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1';

const ACTION_EXTRA = `cursor-pointer font-bold hover:underline hover:decoration-primary-700 hover:decoration-1 hover:underline-offset-8 ${FOCUS_RING}`;

/** Color a panel link has when it is not hovered. */
export type PanelLinkRestingColor = 'subtext' | 'body' | 'black';

const UNDERLINE_HOVER =
  'hover:text-secondary-700 hover:underline hover:decoration-secondary-700 hover:decoration-1 hover:underline-offset-8';

/**
 * `none` repeats the resting color, so the link variant's own hover color is
 * dropped along with the underline.
 */
const NO_HOVER: Record<PanelLinkRestingColor, string> = {
  subtext: 'hover:text-subtext',
  body: 'hover:text-body',
  black: 'hover:text-black',
};

/** Hover classes for an ordinary panel link. */
export function panelLinkHoverClass(
  hover: SubNavLinkHover = 'underline',
  resting: PanelLinkRestingColor = 'subtext',
): string {
  if (hover === 'none') return NO_HOVER[resting];
  if (hover === 'primary') return 'hover:text-primary-700';
  return UNDERLINE_HOVER;
}

/**
 * Variant, size and hover classes for a panel link. `resting` is the color an
 * ordinary link has when not hovered (the caller applies the `body` color);
 * highlighted links always rest black.
 */
export function resolveNavLinkProps(
  item: NavItem,
  resting: PanelLinkRestingColor = 'subtext',
  hover: SubNavLinkHover = 'underline',
): LinkVariantProps & { className: string } {
  if (item.type === 'action') {
    return { variant: 'primary', size: 'sm', className: ACTION_EXTRA };
  }
  if (item.type === 'highlighted') {
    return {
      variant: 'black',
      size: 'md',
      className: `cursor-pointer font-normal ${panelLinkHoverClass(hover, 'black')} ${FOCUS_RING}`,
    };
  }
  return {
    variant: resting === 'black' ? 'black' : 'subtext',
    size: 'sm',
    className: `cursor-pointer ${panelLinkHoverClass(hover, resting)} ${FOCUS_RING}`,
  };
}
